// Service-specific copy used by the reusable CityServicePage template.
// Each service defines hero, pillars, included items, FAQs, and schema metadata
// that get auto-merged with the city's data (name, geo, neighborhoods).

import {
  Search,
  MapPin,
  TrendingUp,
  Smartphone,
  Gauge,
  PenTool,
  Star,
  MessageSquare,
  Camera,
  type LucideIcon,
} from "lucide-react";

// 2026-04: dropped "google-maps" from city services. The /{city}-local-seo
// spoke pages were retired (overlapped too closely with /{city}-seo). The
// /local-seo service hub itself remains live as a top-level service page.
export type CityServiceKey = "seo" | "web-design";

export type CityServiceConfig = {
  key: CityServiceKey;
  /** URL segment used to build canonical: /{routeBase}/{citySlug} */
  routeBase: string;
  /** Service name used in headings, schema, and breadcrumbs */
  serviceName: string;
  /** Schema.org Service.serviceType value */
  serviceType: string;
  /** Used as Service.description in JSON-LD */
  serviceDescription: (cityName: string) => string;
  /** Hero eyebrow tag */
  heroEyebrow: (cityName: string) => string;
  /** Hero gold-highlighted word — usually the city name */
  heroTitle: (cityName: string) => { lead: string; gold: string; trail?: string };
  /** Hero supporting paragraph */
  heroDescription: (cityName: string) => string;
  /** CTA button label */
  ctaLabel: (cityName: string) => string;
  /** Section: "why local X matters" intro */
  whyTitle: (cityName: string) => string;
  whyBody: (cityName: string) => string;
  /** Three pillars shown in the "how we do it" grid */
  pillars: { icon: LucideIcon; title: string; body: (cityName: string) => string }[];
  /** Optional checklist of what's included (Web Design uses this) */
  included?: string[];
  /** Service-specific FAQs (auto-merged with city FAQs) */
  faqs: { q: (cityName: string) => string; a: (cityName: string) => string }[];
  /** Page meta */
  meta: {
    title: (cityName: string) => string;
    description: (cityName: string) => string;
  };
  /** Breadcrumb parent label */
  breadcrumbParent: string;
};

export const CITY_SERVICES: Record<CityServiceKey, CityServiceConfig> = {
  seo: {
    key: "seo",
    routeBase: "seo",
    serviceName: "Local SEO",
    serviceType: "Local SEO",
    serviceDescription: (city) =>
      `Local search engine optimization for ${city} businesses, including Map Pack rankings, on-page SEO, content, links, and review management.`,
    heroEyebrow: (city) => `${city} SEO`,
    heroTitle: (city) => ({
      lead: "Local SEO that ranks ",
      gold: city,
      trail: " businesses where it counts",
    }),
    heroDescription: (city) =>
      `We're a Colorado SEO company that's been helping ${city} businesses rank in the Map Pack and turn search traffic into booked work since 2006.`,
    ctaLabel: (city) => `Get a Free ${city} SEO Audit`,
    whyTitle: (city) => `${city} is a real local market — your SEO should be too`,
    whyBody: () =>
      `Generic "national" SEO misses what makes Colorado markets unique. Map Pack competitors, neighborhood-level search behavior, and seasonal cycles all change the playbook. We've been winning in Colorado for almost 20 years — we know the rhythm.`,
    pillars: [
      {
        icon: MapPin,
        title: "Google Map Pack domination",
        body: (city) =>
          `We tune your Google Business Profile, drive consistent reviews, and build the citations and signals that move you into the top 3 for local ${city} searches.`,
      },
      {
        icon: Search,
        title: "Service-area + neighborhood SEO",
        body: (city) =>
          `Unique pages for the neighborhoods your customers actually live in — so you rank where it matters in ${city}, not just on the city name alone.`,
      },
      {
        icon: TrendingUp,
        title: "On-page + content that earns links",
        body: (city) =>
          `Real, locally written content (no AI filler) that answers the questions ${city} customers ask — and that other Colorado sites are happy to link to.`,
      },
    ],
    faqs: [
      {
        q: (city) => `How much does ${city} SEO cost?`,
        a: (city) =>
          `Most ${city} small businesses invest $1,200–$3,500/month in SEO depending on competition and how many service areas you cover. We publish all our pricing publicly on our pricing page — no 'request a quote' games.`,
      },
      {
        q: (city) => `How long does it take to rank in ${city}?`,
        a: () =>
          `Map Pack movement typically begins in 30–90 days for most local categories. Competitive organic terms usually take 3–6 months of consistent on-page, off-page, and review work.`,
      },
      {
        q: (city) => `Do you guarantee #1 rankings in ${city}?`,
        a: (city) =>
          `No — and you should walk away from any ${city} SEO company that does. Google's own guidelines explicitly forbid ranking guarantees. We guarantee process, transparency, and effort.`,
      },
      {
        q: () => `Will you work on my Google Business Profile too?`,
        a: (city) =>
          `Yes. Google Business Profile (Google Maps) optimization is bundled into every ${city} SEO engagement we run. Reviews, posts, photos, Q&A, and category optimization included.`,
      },
    ],
    meta: {
      title: (city) => `${city} SEO Agency | Local SEO Services | CWI`,
      description: (city) =>
        `Local ${city} SEO services that win the Map Pack and rank you for the searches your customers actually use. Real Colorado team. No long contracts.`,
    },
    breadcrumbParent: "SEO",
  },
  "web-design": {
    key: "web-design",
    routeBase: "web-design",
    serviceName: "Web Design",
    serviceType: "Web Design",
    serviceDescription: (city) =>
      `Custom website design and development for ${city} businesses. Mobile-first, conversion-focused websites with managed hosting and ongoing support.`,
    heroEyebrow: (city) => `${city} Web Design`,
    heroTitle: (city) => ({
      lead: "Web design that converts in ",
      gold: city,
    }),
    heroDescription: (city) =>
      `Custom websites for ${city} businesses — fast, mobile-first, and built to turn local searches into booked work. Headquartered in Colorado since 2006.`,
    ctaLabel: (city) => `Get a Free ${city} Website Proposal`,
    whyTitle: (city) =>
      `A ${city} website should do one thing: bring in real, local work`,
    whyBody: (city) =>
      `Most ${city} business websites are pretty — and don't ring the phone. We design ${city} sites around a single goal: turning a busy local searcher into a phone call, form fill, or booked appointment within seconds. Pretty is a side effect.`,
    pillars: [
      {
        icon: Smartphone,
        title: "Mobile-first, built for the sidewalk",
        body: (city) =>
          `70%+ of ${city} local searches happen on a phone. Every site we ship is designed mobile-first with click-to-call, sticky CTAs, and zero pinch-and-zoom.`,
      },
      {
        icon: Gauge,
        title: "Fast Core Web Vitals",
        body: (city) =>
          `Google ranks fast sites first. We routinely ship ${city} sites under 2-second load times with green Core Web Vitals across mobile and desktop.`,
      },
      {
        icon: PenTool,
        title: "Real local copy that converts",
        body: (city) =>
          `Written by humans who live in Colorado — not AI. We craft copy around the actual reasons ${city} customers pick a business: trust, speed, and local proof.`,
      },
    ],
    included: [
      "Custom design — no cookie-cutter templates",
      "Mobile-first, accessible, and fast",
      "Local SEO foundation built in",
      "Schema markup for Google understanding",
      "Click-to-call + sticky CTA on mobile",
      "Lead capture forms with spam protection",
      "Real photography guidance",
      "Managed hosting + SSL + daily backups",
      "Security monitoring + software updates",
      "Owner training so you're never stuck",
    ],
    faqs: [
      {
        q: (city) => `How much does a website cost in ${city}?`,
        a: (city) =>
          `Most ${city} small businesses spend $4,000–$12,000 for a custom website with us, depending on page count and integrations. We publish all of our pricing publicly — no 'request a quote' games and no surprise upcharges.`,
      },
      {
        q: (city) => `How long does it take to build a ${city} business website?`,
        a: (city) =>
          `Typical timeline is 4–8 weeks from kickoff to launch. Faster if you have content ready; longer if we're writing copy and shooting fresh photography of your ${city} location.`,
      },
      {
        q: () => `Will my new site actually rank on Google?`,
        a: (city) =>
          `We build every ${city} website on a strong technical SEO foundation — fast load times, clean schema, mobile-first design, and proper local on-page signals. SEO and content strategy are a separate ongoing service if you want to actively rank.`,
      },
      {
        q: () => `Do you outsource your web design overseas?`,
        a: (city) =>
          `No. Every ${city} website we build is designed and developed by our local team here in Colorado. No offshore subcontractors, no AI-generated junk content.`,
      },
      {
        q: (city) => `Do you handle hosting, security, and updates for ${city} sites?`,
        a: () =>
          `Yes. Every website we build comes with managed hosting, SSL, daily backups, security monitoring, and ongoing software updates. One predictable monthly fee.`,
      },
    ],
    meta: {
      title: (city) => `${city} Web Design | Custom Websites | CWI`,
      description: (city) =>
        `${city} web design that converts visitors into booked jobs. Fast, mobile-first custom websites built by a real Colorado team. Transparent pricing.`,
    },
    breadcrumbParent: "Web Design",
  },
  // 2026-04: removed "google-maps" entry. The /{city}-local-seo route was
  // retired because it overlapped too closely with /{city}-seo. Local-SEO
  // remains a top-level service hub at /local-seo (not city-specific).
};
